import { useConfiguracion } from '../../../metodos/ConfiguracionContext';
import { useDashboardModel } from '../../../modelos/Dashboard/modeloDashboard'
import React, { useEffect, useState, useMemo } from 'react';
import { 
  CheckCircle2, 
  BookOpen, 
  Layers, 
  Eye, 
  Calendar, 
  ChevronDown, 
  Search,
  Calculator,
  X
} from 'lucide-react';
import { formatearFechaCorta, formatearPrecio } from '../../../utils/utils';
import { obtenerNombreEmpresaReal } from '../../../config/unificacion';

// --- 1. NIVEL FINAL: ITEM INDIVIDUAL ---
const ItemSolicitud = ({ item, getStatusColor }) => {
    return (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 pl-4 border-l-4 border-l-transparent hover:border-l-blue-500 hover:bg-white bg-gray-50/50 transition-all text-sm group border-b border-gray-100 last:border-0">
            
            {/* Info */}
            <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded border uppercase ${getStatusColor(item.estado)}`}>
                        {item.estado}
                    </span>
                    <span className="font-mono text-xs text-gray-400">ID: {item.id}</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                    <Calendar size={12} />
                    <span>Entrega: {formatearFechaCorta(item.fechaentrega)}</span>
                </div>
                 <div className="flex items-center gap-1.5 text-xs text-gray-500">
                    <Calendar size={12} />
                    <span>Semestre: {item.semestre }</span>
                </div>
            </div>

            {/* Precio Individual y Botón */}
            <div className="flex items-center gap-4 justify-between sm:justify-end mt-2 sm:mt-0">
                {/* Mostramos el precio individual, aunque sea 0, para referencia */}
                <span className={`font-bold text-sm ${item.precioMateria === 0 ? 'text-gray-300' : 'text-gray-700'}`}>
                    {formatearPrecio(item.precioMateria)}
                </span>
                
                <button 
                    onClick={() => console.log("Ver solicitud", item.id)}
                    className="flex items-center gap-1.5 bg-blue-50 hover:bg-blue-600 hover:text-white text-blue-600 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors border border-blue-100 hover:border-blue-600"
                >
                    <Eye size={14} />
                    Ver Solicitud
                </button>
            </div>
        </div>
    );
};

// --- 2. NIVEL INTERMEDIO: CARD DE FASE ---
const FaseCard = ({ nombreFase, items, precioFaseCalculado, getStatusColor }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div className="border border-gray-200 rounded-lg mb-2 overflow-hidden bg-white shadow-sm">
            <div 
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center justify-between p-3 px-4 cursor-pointer hover:bg-gray-50 transition-colors select-none"
            >
                {/* Nombre Fase */}
                <div className="flex items-center gap-3">
                    <div className={`p-1.5 rounded-md transition-colors ${isOpen ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'}`}>
                        <Layers size={16} />
                    </div>
                    <div>
                        <h4 className="font-semibold text-gray-800 text-sm">{nombreFase}</h4>
                        <p className="text-[10px] text-gray-400">{items.length} items registrados</p>
                    </div>
                </div>

                {/* Precio Fase (PROMEDIO) */}
                <div className="flex items-center gap-4">
                    <div className="text-right">
                        <span className="text-sm font-bold text-emerald-700 block">
                            {formatearPrecio(precioFaseCalculado)}
                        </span>
                        <span className="text-[9px] text-gray-400 uppercase tracking-wider flex items-center justify-end gap-1">
                            Precio Estimado <Calculator size={8}/>
                        </span>
                    </div>
                    <ChevronDown size={16} className={`text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                </div>
            </div>

            {/* Lista Items */}
            {isOpen && (
                <div className="border-t border-gray-100">
                    {items.map((item, idx) => (
                        <ItemSolicitud 
                            key={`${item.id}-${idx}`} 
                            item={item} 
                            getStatusColor={getStatusColor} 
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

// --- 3. NIVEL SUPERIOR: CARD DE MATERIA ---
const MateriaCard = ({ materia, getStatusColor }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    // El Total de la tarjeta ahora es la suma de los "Precios de Fase" (que son promedios)
    const totalMateriaCalculado = materia.fases.reduce((acc, fase) => acc + fase.precioFaseCalculado, 0);
    const cantidadFases = materia.fases.length;

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-all duration-200 hover:shadow-md mb-4">
            
            {/* Header Materia */}
            <div 
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-5 cursor-pointer bg-white flex items-center justify-between group select-none"
            >
                <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${isExpanded ? 'bg-blue-600 text-white' : 'bg-blue-600'}`}>
                        <BookOpen size={20} />
                    </div>
                    <div>
                        <h3 className="text-lg font-bold text-gray-800 group-hover:text-blue-700 transition-colors">
                            {materia.nombre}
                        </h3>
                        <div className="text-xs text-gray-500 font-medium flex items-center gap-2 mt-1">
                            <span className="bg-gray-100 px-2 py-0.5 rounded text-gray-600 border border-gray-200">
                                {cantidadFases} Fases
                            </span>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-4">
                    {/* Total Materia (Suma de promedios) */}
                    <span className="text-emerald-600 font-bold text-base hidden sm:block bg-emerald-50 px-3 py-1 rounded-lg border border-emerald-100">
                        {formatearPrecio(totalMateriaCalculado)}
                    </span>
                    <ChevronDown size={24} className={`text-gray-400 transition-transform duration-300 ${isExpanded ? 'rotate-180 text-blue-600' : ''}`} />
                </div>
            </div>

            {/* Contenido Materia */}
            {isExpanded && (
                <div className="border-t border-gray-100 bg-gray-50 p-4 animate-in slide-in-from-top-2 duration-200">
                    {materia.fases.map((fase, idx) => (
                        <FaseCard 
                            key={`${fase.nombreFase}-${idx}`} 
                            nombreFase={fase.nombreFase} 
                            items={fase.items} 
                            precioFaseCalculado={fase.precioFaseCalculado} // Pasamos el promedio calculado
                            getStatusColor={getStatusColor}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

const ProPlataformaDashboard = () => {

    const {
        datosplataforma,
        Loadingdatosplataforma,
        errordatosplataforma,
        cargarDashboardPlataforma
    } = useDashboardModel();

    const {configuracion} = useConfiguracion();
    let nombreEmpresaReal =  obtenerNombreEmpresaReal(configuracion.nombreEmpresa);

    useEffect(() =>{
        cargarDashboardPlataforma(nombreEmpresaReal,"");
    },[]);

    // ESTADO PARA EL BUSCADOR
    const [busqueda, setBusqueda] = useState("");

    //CARGA DE FASES
    const datosEstructurados = useMemo(() => {
        if (!datosplataforma?.data?.porFases) return [];

        const dataFases = datosplataforma.data.porFases;

        // 1. Procesar todo (Calcular precios, ordenar, etc)
        const listaCompleta = Object.entries(dataFases).map(([nombreMateria, fasesObj]) => {
            
            const fasesArray = Object.entries(fasesObj).map(([nombreFase, itemsArray]) => {
                const itemsSeguros = Array.isArray(itemsArray) ? itemsArray : [];
                
                // Promedio
                const itemsConPrecio = itemsSeguros.filter(item => item.precioMateria > 0);
                const sumaPrecios = itemsConPrecio.reduce((acc, curr) => acc + curr.precioMateria, 0);
                const precioPromedioCalculado = itemsConPrecio.length > 0 ? (sumaPrecios / itemsConPrecio.length) : 0;

                // Ordenar Items
                const itemsOrdenados = [...itemsSeguros].sort((a, b) => (a.fechaentrega?._seconds || 0) - (b.fechaentrega?._seconds || 0));

                return {
                    nombreFase,
                    items: itemsOrdenados,
                    precioFaseCalculado: precioPromedioCalculado
                };
            });

            // Ordenar Fases
            fasesArray.sort((a, b) => a.nombreFase.localeCompare(b.nombreFase, undefined, { numeric: true, sensitivity: 'base' }));

            return {
                nombre: nombreMateria,
                fases: fasesArray
            };
        });

        // 2. FILTRAR SEGÚN BÚSQUEDA (Si hay texto en el input)
        if (busqueda.trim() !== "") {
            return listaCompleta.filter(materia => 
                materia.nombre.toLowerCase().includes(busqueda.toLowerCase())
            );
        }

        return listaCompleta;

    }, [datosplataforma, busqueda]);

    const getStatusColor = (estado) => {
        const est = estado?.toUpperCase();
        if (est === 'AGENDADO') return 'text-blue-600 bg-blue-50 border-blue-200';
        if (est === 'EXPIRADO') return 'text-red-600 bg-red-50 border-red-200';
        return 'text-gray-600 bg-gray-50 border-gray-200';
    };

    if(Loadingdatosplataforma){
        return <div className="bg-white rounded-xl shadow-lg p-6 h-96 flex items-center justify-center">Cargando trabajos... ⏳</div>
    }

    if(errordatosplataforma){
        return <div className="bg-white rounded-xl shadow-lg p-6 h-96 flex items-center justify-center text-red-500">Error al cargar datos. ❌</div>;
    }

    return (
        <div className="bg-gray-50 min-h-screen p-6 font-sans">
            <div className="max-w-4xl mx-auto">
                
                {/* ENCABEZADO CON BUSCADOR */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                    
                    {/* Título */}
                    <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                        <CheckCircle2 className="text-blue-600" />
                        Materias Inscritas
                        <span className="text-sm font-normal text-gray-500 bg-white px-3 py-1 rounded-full border border-gray-200 ml-2 shadow-sm">
                            {datosEstructurados.length}
                        </span>
                    </h1>

                    {/* Buscador */}
                    <div className="relative w-full md:w-72">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search size={16} className="text-gray-400" />
                        </div>
                        <input 
                            type="text"
                            placeholder="Buscar materia..."
                            className="w-full pl-10 pr-10 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all shadow-sm"
                            value={busqueda}
                            onChange={(e) => setBusqueda(e.target.value)}
                        />
                        {/* Botón 'X' para limpiar búsqueda si hay texto */}
                        {busqueda && (
                            <button 
                                onClick={() => setBusqueda("")}
                                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                            >
                                <X size={14} />
                            </button>
                        )}
                    </div>
                </div>

                {/* LISTA DE MATERIAS */}
                <div className="space-y-4">
                    {datosEstructurados.map((materia, index) => (
                        <MateriaCard 
                            key={`${materia.nombre}-${index}`} 
                            materia={materia} 
                            getStatusColor={getStatusColor} 
                        />
                    ))}

                    {datosEstructurados.length === 0 && (
                        <div className="text-center py-16 bg-white border-2 border-dashed border-gray-200 rounded-xl flex flex-col items-center">
                            <Search className="h-12 w-12 text-gray-300 mb-3" />
                            <p className="text-gray-500 font-medium">
                                {busqueda ? `No encontramos materias llamadas "${busqueda}"` : "No hay materias disponibles."}
                            </p>
                            {busqueda && (
                                <button 
                                    onClick={() => setBusqueda("")}
                                    className="mt-3 text-blue-600 text-sm hover:underline font-medium"
                                >
                                    Limpiar búsqueda
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

}

export default ProPlataformaDashboard
